<!-- src/views/ListDevicesPage.vue -->
<template>
  <div class="page-wrapper">
    <div class="page-content" dir="rtl">
      <!-- ========== UPDATE PROMPT (NEW DESIGN) ========== -->
      <Transition
        enter-active-class="animate__animated animate__bounceIn"
        leave-active-class="animate__animated animate__fadeOut"
      >
        <div v-if="showUpdatePrompt" class="update-overlay">
          <div class="update-card">
            <div class="update-icon">
              <i class="fas fa-sync-alt fa-spin"></i>
            </div>
            <h2 class="update-title">نسخه جدید در دسترس است!</h2>
            <div v-if="newVersion" class="update-badge">
              <span>نسخه جدید:</span>
              <code>{{ newVersion }}</code>
            </div>
            <p class="update-message">
              برای استفاده از آخرین ویژگی‌ها و رفع مشکلات، لطفاً برنامه را
              بارگذاری مجدد کنید.
            </p>
            <div class="update-buttons">
              <button @click="handleUpdate" class="btn-update">
                <i class="fas fa-arrow-circle-down"></i>
                <span>بارگذاری مجدد</span>
              </button>
              <button @click="showUpdatePrompt = false" class="btn-later">
                <i class="fas fa-clock"></i>
                <span>بعداً</span>
              </button>
            </div>
          </div>
        </div>
      </Transition>

      <!-- BackgroundBox -->
      <div
        class="background-box"
        :style="{
          filter: showDialog ? 'blur(30px)' : 'none',
          'background-color': showDialog ? 'rgba(0, 0, 0, 0.2)' : '#25273e',
        }"
      >
        <!-- Header with Logo -->
        <div class="header">
          <img
            :src="`${baseUrl}img/screen_logo.webp`"
            alt="تیراژه لوگو"
            class="header-logo"
          />
        </div>

        <!-- Device List Container -->
        <div class="device-list-container">
          <!-- Loading Indicator -->
          <div v-if="isLoading" class="loading-indicator">
            <div class="spinner"></div>
          </div>

          <!-- Empty State -->
          <div v-else-if="devices.length === 0" class="empty-state">
            <p>هنوز دستگاهی اضافه نشده است</p>
          </div>

          <!-- Device List -->
          <div v-else class="device-list">
            <div
              v-for="(device, index) in devices"
              :key="device.id"
              class="device-item"
              @click="navigateToDetails(device.id)"
            >
              <span class="row-number">{{
                $utils.toPersianDigits(`${index + 1}-`)
              }}</span>
              <img
                :src="$utils.getDeviceIcon(device.type)"
                class="device-icon"
                :alt="`آیکون دستگاه ${device.type}`"
              />
              <span class="device-name">{{
                $utils.toPersianDigits(device.name)
              }}</span>
              <div class="device-actions">
                <button class="edit-button" @click.stop="editDevice(device.id)">
                  <img
                    :src="`${baseUrl}img/icons/ic_edit.webp`"
                    class="action-icon"
                    alt="ویرایش"
                  />
                </button>
                <button
                  class="delete-button"
                  @click.stop="showDeleteDialog(device.id)"
                >
                  <img
                    :src="`${baseUrl}img/icons/ic_delete.webp`"
                    class="action-icon"
                    alt="حذف"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="app-version">نسخه {{ displayedVersion }}</div>

        <!-- Add Button -->
        <button
          class="add-button"
          @click="$router.push('/device/add')"
          aria-label="افزودن دستگاه"
        >
          <img
            :src="`${baseUrl}img/icons/plus.webp`"
            alt="اضافه کردن"
            class="add-icon"
          />
        </button>
      </div>

      <!-- Delete Dialog -->
      <div v-if="showDialog" class="dialog-overlay">
        <div class="dialog-box">
          <h2>حذف</h2>
          <p>آیا مطمئن هستید که می‌خواهید این دستگاه را حذف کنید؟</p>
          <div class="dialog-buttons">
            <button @click="confirmDelete" class="confirm-button">
              <span>تأیید</span>
              <img
                :src="`${baseUrl}img/icons/save.webp`"
                alt="تأیید"
                class="button-icon"
              />
            </button>
            <button @click="hideDialog" class="dismiss-button">
              <span>لغو</span>
              <img
                :src="`${baseUrl}img/icons/cancel.webp`"
                alt="لغو"
                class="button-icon"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { globalState } from "../store";

export default {
  name: "ListDevicesPage",

  data() {
    let devices = [];
    try {
      const storedDevices = localStorage.getItem("devices");
      devices = storedDevices ? JSON.parse(storedDevices) : [];
      if (!Array.isArray(devices)) devices = [];

      devices = devices.map((device) => ({
        ...device,
        phoneNumber: device.phoneNumber || "",
        pin: device.pin || "",
        type: device.type || "home",
        name: device.name || "دستگاه بدون نام",
      }));

      devices = devices.map(({ status, ...rest }) => rest);
      localStorage.setItem("devices", JSON.stringify(devices));
    } catch (error) {
      console.error("Error parsing devices from localStorage:", error);
      devices = [];
    }

    return {
      devices,
      showUpdatePrompt: false,
      newVersion: null,
      currentCacheName: localStorage.getItem("cacheName") || "",
      waitingServiceWorker: null,
      updateCheckInterval: null,
      isLoading: true,
      showDialog: false,
      deviceToDelete: null,
      isCheckingUpdate: false,
    };
  },

  computed: {
    baseUrl() {
      return process.env.BASE_URL || "/";
    },

    isDev() {
      return process.env.NODE_ENV === "development";
    },

    displayedVersion() {
      if (this.isDev) {
        if (!this.currentCacheName) {
          return "DEV · local";
        }
        return `DEV · ${this.currentCacheName}`;
      }

      return process.env.APP_VERSION || "unknown";
    },
  },

  methods: {
    handleBackButton(e) {
      if (e) {
        e.preventDefault();
      }

      if (this.showDialog) {
        this.hideDialog();
        return;
      }

      if (this.showUpdatePrompt) {
        this.showUpdatePrompt = false;
        return;
      }

      if (window.cordova && navigator.app && navigator.app.exitApp) {
        navigator.app.exitApp();
        return;
      }

      if (window.close) {
        window.close();
      }

      setTimeout(() => {
        if (!document.hidden) {
          window.location.href = "about:blank";
        }
      }, 100);
    },

    navigateToDetails(id) {
      this.$router.push(`/device/details/${id}`);
    },

    editDevice(id) {
      globalState.setEditingDeviceId(id);
      this.$router.push("/device/edit");
    },

    showDeleteDialog(id) {
      this.deviceToDelete = id;
      this.showDialog = true;
    },

    hideDialog() {
      this.showDialog = false;
      this.deviceToDelete = null;
    },

    confirmDelete() {
      if (this.deviceToDelete) {
        // ✅ حذف دستگاه از لیست devices
        this.devices = this.devices.filter((d) => d.id !== this.deviceToDelete);
        localStorage.setItem("devices", JSON.stringify(this.devices));

        // ✅ حذف تمام داده‌های مرتبط با این دستگاه از localStorage
        const keysToRemove = [];

        // پیدا کردن تمام کلیدهایی که با deviceId شروع می‌شوند
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);

          // الگوهای مختلف ذخیره‌سازی داده‌ها
          if (
            key.startsWith(`device_${this.deviceToDelete}_`) || // مثال: device_123_zones
            key.startsWith(`${this.deviceToDelete}_`) || // مثال: 123_zones
            key.includes(`_${this.deviceToDelete}_`) || // مثال: sms_123_logs
            key === `device_${this.deviceToDelete}` || // مثال: device_123
            key === `zones_${this.deviceToDelete}` || // زون‌ها
            key === `remotes_${this.deviceToDelete}` || // ریموت‌ها
            key === `logs_${this.deviceToDelete}` || // لاگ‌ها
            key === `sms_${this.deviceToDelete}` || // پیام‌ها
            key === `settings_${this.deviceToDelete}` || // تنظیمات
            key === `passwords_${this.deviceToDelete}` || // رمزها
            key === `users_${this.deviceToDelete}` || // کاربران
            key === `deviceProperties_${this.deviceToDelete}` // خصوصیات دستگاه
          ) {
            keysToRemove.push(key);
          }
        }

        // حذف تمام کلیدهای یافت‌شده
        keysToRemove.forEach((key) => {
          localStorage.removeItem(key);
          console.log(`🗑️ Removed: ${key}`);
        });

        console.log(
          `✅ Device ${this.deviceToDelete} and all related data deleted`
        );

        this.hideDialog();
      }
    },

    async handleUpdate() {
      try {
        console.log("🔄 Update button clicked");
        this.showUpdatePrompt = false;
        await new Promise((resolve) => setTimeout(resolve, 300));
        console.log(
          "📤 Calling reloadApp with waitingServiceWorker:",
          this.waitingServiceWorker
        );
        await this.$utils.reloadApp(this.waitingServiceWorker);
      } catch (error) {
        console.error("❌ Failed to reload app:", error);
        this.showUpdatePrompt = true;
      }
    },

    async checkForServiceWorkerUpdates() {
      if (this.isCheckingUpdate || this.showUpdatePrompt) {
        return;
      }

      this.isCheckingUpdate = true;

      try {
        if ("serviceWorker" in navigator) {
          const registration = await navigator.serviceWorker.getRegistration();

          if (!registration) {
            return;
          }

          const waitingSW = registration.waiting;

          if (waitingSW && !this.waitingServiceWorker) {
            await this.handleNewServiceWorker(waitingSW);
          }

          await registration.update();
        }
      } catch (error) {
        console.error("Failed to check for service worker updates:", error);
      } finally {
        this.isCheckingUpdate = false;
      }
    },

    async handleNewServiceWorker(serviceWorker) {
      try {
        console.log("✅ New service worker detected:", serviceWorker);
        this.waitingServiceWorker = serviceWorker;

        try {
          const manifest = await fetch("/manifest.json?t=" + Date.now()).then(
            (r) => r.json()
          );
          this.newVersion = manifest.version;
          console.log("📦 New version:", this.newVersion);
        } catch (error) {
          console.error("Failed to fetch manifest version:", error);
          this.newVersion = "جدید";
        }

        this.showUpdatePrompt = true;
        console.log("💬 Update prompt shown");
      } catch (error) {
        console.error("Failed to handle new service worker:", error);
      }
    },

    setupServiceWorkerListeners(registration) {
      if (this._updateFoundHandler) {
        registration.removeEventListener(
          "updatefound",
          this._updateFoundHandler
        );
      }

      this._updateFoundHandler = () => {
        const newWorker = registration.installing;
        console.log("🔍 Update found, new worker:", newWorker);

        if (newWorker) {
          if (this._stateChangeHandler) {
            newWorker.removeEventListener(
              "statechange",
              this._stateChangeHandler
            );
          }

          this._stateChangeHandler = async () => {
            console.log("📊 Worker state changed:", newWorker.state);
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller &&
              !this.waitingServiceWorker
            ) {
              await this.handleNewServiceWorker(newWorker);
            }
          };

          newWorker.addEventListener("statechange", this._stateChangeHandler);
        }
      };

      registration.addEventListener("updatefound", this._updateFoundHandler);
    },

    async initServiceWorker() {
      // ✅ در محیط Development، Service Worker را نادیده بگیر
      if (this.isDev) {
        console.log("🚧 Service Worker disabled in development mode");
        return;
      }

      if (!("serviceWorker" in navigator)) {
        console.log("⚠️ Service Worker not supported");
        return;
      }

      try {
        const registration = await navigator.serviceWorker.getRegistration();

        if (!registration) {
          console.log("⏳ Service Worker not registered yet");
          return;
        }

        console.log("✅ Service Worker registration found:", registration);

        const waitingSW = registration.waiting;

        if (waitingSW) {
          console.log("⏳ Found waiting service worker");
          await this.handleNewServiceWorker(waitingSW);
        }

        this.setupServiceWorkerListeners(registration);

        await registration.update();

        this.updateCheckInterval = setInterval(() => {
          this.checkForServiceWorkerUpdates();
        }, 5 * 60 * 1000);

        console.log("✅ Service Worker initialized successfully");
      } catch (error) {
        console.error("❌ Service Worker initialization failed:", error);
      }
    },
  },

  async mounted() {
    setTimeout(() => {
      this.isLoading = false;
    }, 200);

    await this.initServiceWorker();

    document.addEventListener("backbutton", this.handleBackButton, false);
    window.addEventListener("popstate", this.handleBackButton);

    // ✅ حفظ state موجود Vue Router
    history.pushState(
      { ...history.state, preventBack: true },
      "",
      location.href
    );
  },

  beforeUnmount() {
    document.removeEventListener("backbutton", this.handleBackButton, false);
    window.removeEventListener("popstate", this.handleBackButton);

    if (this.updateCheckInterval) {
      clearInterval(this.updateCheckInterval);
    }

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration && this._updateFoundHandler) {
          registration.removeEventListener(
            "updatefound",
            this._updateFoundHandler
          );
        }
      });
    }
  },
};
</script>

<style scoped>
* {
  box-sizing: border-box;
}

/* ✅ Wrapper با background ثابت برای دسکتاپ/تبلت */
.page-wrapper {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background-color: #1a1a2e;
  padding: 0;
  overflow-y: auto;
}

.page-content {
  font-family: "Vazir";
  margin: 0;
  padding: 0;
  width: 100%;
  max-width: 768px;
  min-height: 100vh;
  min-height: 100dvh;
  position: relative;
  direction: rtl;
  background-color: #25273e;
  box-shadow: 0 0 40px rgba(0, 0, 0, 0.6);
}

/* ✅ تبلت عمودی (Portrait) */
@media (min-width: 481px) and (max-width: 1024px) and (orientation: portrait) {
  .page-content {
    max-width: 768px;
  }
}

/* ✅ تبلت افقی (Landscape) */
@media (min-width: 481px) and (max-width: 1024px) and (orientation: landscape) {
  .page-content {
    max-width: 90vw;
  }
}

/* ✅ دسکتاپ - محدودیت عرض */
@media (min-width: 1025px) {
  .page-content {
    max-width: 480px;
  }
}

/* ✅ موبایل واقعی - تمام صفحه */
@media (max-width: 480px) {
  .page-wrapper {
    background-color: #25273e;
    padding: 0;
    align-items: stretch;
  }

  .page-content {
    max-width: 100%;
    width: 100%;
    box-shadow: none;
    min-height: 100vh;
    min-height: 100dvh;
  }
}

.background-box {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  background-color: #25273e;
  position: relative;
  transition: filter 0.3s;
  display: flex;
  flex-direction: column;
}

.device-list-container {
  background-color: #80808e;
  border-radius: 84px 84px 0 0;
  flex: 1 1 auto;
  min-height: 0;
  padding: 24px 4px 0 4px;
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.header {
  height: 16vh;
  min-height: 80px;
  max-height: 140px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
  flex-shrink: 0;
  background-color: #25273e;
}

.header-logo {
  max-height: 100%;
  max-width: 90%;
  width: auto;
  object-fit: contain;
}

.loading-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #2b2940;
  border-top: 4px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.empty-state {
  text-align: center;
  color: #ffffff;
  font-size: 16px;
  padding: 24px;
  margin-top: 48px;
}

.device-list {
  flex: 1;
  overflow-y: auto;
  padding: 0 32px 120px 32px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.device-list::-webkit-scrollbar {
  display: none;
}

.device-item {
  display: flex;
  align-items: center;
  background-color: transparent;
  padding: 0;
  margin-bottom: 8px;
  border: 1px solid #2b2940;
  border-radius: 24px;
  height: 48px;
  cursor: pointer;
  transition: background-color 0.2s;
  flex-shrink: 0;
}

.device-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.row-number {
  font-size: 16px;
  color: #ffffff;
  padding: 0 16px 0 0;
  width: 15%;
  flex-shrink: 0;
}

.device-icon {
  width: 28px;
  height: 28px;
  margin: 0 10px;
  filter: brightness(0) saturate(100%) hue-rotate(180deg);
  flex-shrink: 0;
}

.device-name {
  font-size: 16px;
  color: #ffffff;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.device-actions {
  display: flex;
  gap: 4px;
  padding-right: 8px;
  padding-left: 16px;
  flex-shrink: 0;
}

.action-icon {
  width: 24px;
  height: 24px;
  filter: brightness(0) saturate(100%) hue-rotate(180deg);
}

.edit-button,
.delete-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
}

.app-version {
  position: absolute;
  bottom: 100px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  user-select: none;
  z-index: 10;
}

.add-button {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 64px;
  height: 64px;
  background-color: #2bb04d;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  z-index: 10;
  flex-shrink: 0;
}

.add-icon {
  width: 32px;
  height: 32px;
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(30px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 8px;
}

.dialog-box {
  background-color: #2b2940;
  padding: 16px;
  border-radius: 16px;
  width: 100%;
  max-width: 80%;
  text-align: center;
}

.dialog-box h2 {
  font-size: 24px;
  font-weight: bold;
  color: #ffffff;
  margin: 0 0 8px;
}

.dialog-box p {
  font-size: 16px;
  color: #d3d3d3;
  margin: 0 0 16px;
}

.dialog-buttons {
  display: flex;
  justify-content: space-evenly;
  gap: 10px;
  padding-top: 16px;
}

.confirm-button,
.dismiss-button {
  background-color: #27253e;
  color: #ffffff;
  border: none;
  border-radius: 48px;
  padding: 10px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: bold;
}

.button-icon {
  width: 32px;
  height: 32px;
}

/* ========== UPDATE PROMPT STYLES ========== */

.update-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(10px);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  box-sizing: border-box;
}

.update-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 24px;
  padding: 32px 24px;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(102, 126, 234, 0.4);
  text-align: center;
  animation: float 3s ease-in-out infinite;
  box-sizing: border-box;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
}

.update-icon {
  font-size: 64px;
  color: #ffffff;
  margin-bottom: 20px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.update-title {
  font-size: 24px;
  font-weight: bold;
  color: #ffffff;
  margin: 0 0 16px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.update-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 8px 16px;
  margin-bottom: 16px;
  backdrop-filter: blur(10px);
}

.update-badge span {
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
}

.update-badge code {
  background: rgba(0, 0, 0, 0.3);
  color: #ffd700;
  padding: 4px 12px;
  border-radius: 12px;
  font-family: "Courier New", monospace;
  font-size: 13px;
  font-weight: bold;
}

.update-message {
  color: rgba(255, 255, 255, 0.95);
  font-size: 15px;
  line-height: 1.6;
  margin: 0 0 24px;
}

.update-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-update,
.btn-later {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Vazir";
  white-space: nowrap;
}

.btn-update {
  background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
  color: #ffffff;
  box-shadow: 0 4px 15px rgba(56, 239, 125, 0.4);
}

.btn-update:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(56, 239, 125, 0.6);
}

.btn-update:active {
  transform: translateY(0);
}

.btn-later {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  backdrop-filter: blur(10px);
}

.btn-later:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.btn-later:active {
  transform: translateY(0);
}

.btn-update i,
.btn-later i {
  font-size: 18px;
}

/* Responsive Design */
@media (max-width: 480px) {
  .update-card {
    padding: 24px 16px;
    max-width: calc(100% - 32px);
  }

  .update-title {
    font-size: 20px;
  }

  .update-buttons {
    flex-direction: column;
    width: 100%;
  }

  .btn-update,
  .btn-later {
    width: 100%;
    justify-content: center;
  }
}

@media (max-height: 700px) {
  .header {
    height: 12vh;
    min-height: 60px;
  }
}
</style>
